import React, { useState } from 'react';
import { Row, Col, Card, Button, Form, Input, Typography, message, Modal, Divider } from 'antd';
import { useNavigate } from 'react-router-dom';
import { getUserData, saveUserData } from '../constants';
import axiosInstance from '../../axiosInstance';
import { 
  UserOutlined, 
  LockOutlined, 
  MailOutlined,
  ArrowRightOutlined,
  LoadingOutlined,
  ShoppingCartOutlined
} from '@ant-design/icons';

const { Text, Title } = Typography;

const Login = () => {
    const [loading, setLoading] = useState(false);
    const [isForgotPasswordModalVisible, setIsForgotPasswordModalVisible] = useState(false);
    const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    const navigate = useNavigate();

    const handleSubmit = (values) => {
        setLoading(true);

        axiosInstance.post('login', values)
            .then(response => {
                if (response.status === 200) {
                    const { user, token } = response.data;

                    localStorage.setItem('token', token);
                    const shops = user.employees?.map(employee => employee.shop) || [];
                    saveUserData({
                        id: user.id,
                        name: user.name,
                        email: user.email,
                        phone: user.phone,
                        profile_pic: user.profile_pic,
                        role: user.role,
                        shops: shops,
                        user_permissions: user.user_permission
                    });

                    const userData = getUserData() || {};
                    const userShops = userData?.shops || [];
                    const selectedShop = userShops.length > 0 ? userShops[0] : null;
                    localStorage.setItem('employeeId', user.id);

                    if (selectedShop) {
                        localStorage.setItem('shopId', selectedShop.id);
                        message.success(`Default shop set to: ${selectedShop.name}`);
                    }

                    navigate('/home');
                    window.location.reload();
                }
            })
            .catch(error => {
                if (error.response && error.response.status === 401) {
                    message.error('Invalid credentials');
                } else if (error.response && error.response.status === 422) {
                    message.error('Validation error');
                } else {
                    message.error('An error occurred. Please try again later.');
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleForgotPassword = (values) => {
        setForgotPasswordLoading(true);

        axiosInstance.post('reset-password', values)
            .then(response => {
                if (response.status === 200) {
                    message.success(response.data.message);
                    setIsForgotPasswordModalVisible(false);
                }
            })
            .catch(error => {
                if (error.response && error.response.status === 404) {
                    message.error('Email not found.');
                } else if (error.response && error.response.status === 422) {
                    const errors = error.response.data.errors;
                    if (errors.phone) {
                        message.error(errors.phone[0]);
                    } else {
                        message.error('Validation error.');
                    }
                } else {
                    message.error('An error occurred. Please try again later.');
                }
            })
            .finally(() => {
                setForgotPasswordLoading(false);
            });
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px'
        }}>
            <Row justify="center" style={{ width: '100%' }}>
                <Col 
                    xl={8} 
                    lg={10} 
                    md={12} 
                    sm={16} 
                    xs={24}
                    style={{
                        transition: 'all 0.3s ease',
                        transform: isHovered ? 'translateY(-5px)' : 'none'
                    }}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    <Card 
                        style={{
                            padding: '40px 30px',
                            borderRadius: '16px',
                            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                            border: 'none',
                            background: 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(10px)',
                            WebkitBackdropFilter: 'blur(10px)'
                        }}
                        bodyStyle={{ padding: 0 }}
                    >
                        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                            <div style={{ 
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: '15px',
                                color: '#1890ff'
                            }}>
                                <ShoppingCartOutlined style={{ 
                                    fontSize: '40px',
                                    padding: '10px',
                                    background: 'rgba(24, 144, 255, 0.1)',
                                    borderRadius: '50%'
                                }} />
                            </div>
                            <Title level={3} style={{ 
                                marginBottom: '5px',
                                color: '#1890ff',
                                fontWeight: 600
                            }}>
                                Emmanuel POS
                            </Title>
                            <Text type="secondary" style={{ fontSize: '14px' }}>
                                Point of Sale System
                            </Text>
                        </div>

                        <Form layout="vertical" onFinish={handleSubmit}>
                            <Form.Item
                                name="email"
                                rules={[
                                    { required: true, message: 'Please enter your email' },
                                    { type: 'email', message: 'Please enter a valid email' },
                                ]}
                                style={{ marginBottom: '20px' }}
                            >
                                <Input
                                    prefix={<MailOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                                    placeholder="Email address"
                                    size="large"
                                    style={{
                                        borderRadius: '8px',
                                        padding: '10px 15px',
                                        height: '48px'
                                    }}
                                />
                            </Form.Item>

                            <Form.Item
                                name="password"
                                rules={[{ required: true, message: 'Please enter your password' }]}
                                style={{ marginBottom: '5px' }}
                            >
                                <Input.Password
                                    prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                                    placeholder="Password"
                                    size="large"
                                    style={{
                                        borderRadius: '8px',
                                        padding: '10px 15px',
                                        height: '48px'
                                    }}
                                />
                            </Form.Item>

                            <div style={{ 
                                display: 'flex', 
                                justifyContent: 'flex-end',
                                marginBottom: '20px'
                            }}>
                                <Text 
                                    style={{ 
                                        color: '#1890ff',
                                        cursor: 'pointer',
                                        fontSize: '14px'
                                    }}
                                    onClick={() => setIsForgotPasswordModalVisible(true)}
                                >
                                    Forgot password?
                                </Text>
                            </div>

                            <Form.Item style={{ marginBottom: '15px' }}>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={loading}
                                    size="large"
                                    block
                                    style={{
                                        height: '48px',
                                        borderRadius: '8px',
                                        fontWeight: 500,
                                        fontSize: '16px',
                                        boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)',
                                        border: 'none'
                                    }}
                                    icon={loading ? <LoadingOutlined /> : <ArrowRightOutlined />}
                                >
                                    {loading ? 'Signing in...' : 'Sign In'}
                                </Button>
                            </Form.Item>
                        </Form>

                        <Divider style={{ 
                            color: 'rgba(0, 0, 0, 0.25)',
                            fontSize: '14px',
                            margin: '20px 0'
                        }}>
                            OR
                        </Divider>

                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'center',
                            gap: '15px'
                        }}>
                            <Button 
                                shape="circle" 
                                size="large"
                                style={{
                                    width: '48px',
                                    height: '48px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M12 8V16" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M8 12H16" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </Button>
                            <Button 
                                shape="circle" 
                                size="large"
                                style={{
                                    width: '48px',
                                    height: '48px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M12 8V16" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M8 12H16" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </Button>
                            <Button 
                                shape="circle" 
                                size="large"
                                style={{
                                    width: '48px',
                                    height: '48px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M12 8V16" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M8 12H16" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </Button>
                        </div>
                    </Card>

                    <div style={{ 
                        textAlign: 'center', 
                        marginTop: '20px',
                        color: 'rgba(0, 0, 0, 0.45)',
                        fontSize: '14px'
                    }}>
                        © {new Date().getFullYear()} Emmanuel POS. All rights reserved.
                    </div>
                </Col>
            </Row>

            <Modal
                title={<span style={{ fontWeight: 500 }}>Reset Password</span>}
                visible={isForgotPasswordModalVisible}
                onCancel={() => setIsForgotPasswordModalVisible(false)}
                footer={null}
                centered
                destroyOnClose
            >
                <Form layout="vertical" onFinish={handleForgotPassword}>
                    <Text style={{ 
                        display: 'block',
                        marginBottom: '20px',
                        color: 'rgba(0, 0, 0, 0.65)'
                    }}>
                        Enter your email address and we'll send you a link to reset your password.
                    </Text>

                    <Form.Item
                        name="email"
                        rules={[{ required: true, message: 'Please enter your email' }]}
                        style={{ marginBottom: '24px' }}
                    >
                        <Input
                            prefix={<MailOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                            placeholder="Email address"
                            size="large"
                            style={{
                                borderRadius: '8px',
                                padding: '10px 15px',
                                height: '48px'
                            }}
                        />
                    </Form.Item>

                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={forgotPasswordLoading}
                        size="large"
                        block
                        style={{
                            height: '48px',
                            borderRadius: '8px',
                            fontWeight: 500,
                            fontSize: '16px',
                            boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)',
                            border: 'none'
                        }}
                    >
                        {forgotPasswordLoading ? 'Sending...' : 'Send Reset Link'}
                    </Button>
                </Form>
            </Modal>
        </div>
    );
};

export default Login;